import { delay } from "../client.js";

const STORAGE_KEY = "console-design.sims.v1";

export const SIM_STATUSES = ["active", "suspended", "inactive"];

const SEED_SIMS = [
  {
    id: "sim-1",
    iccid: "8991100000000000001",
    msisdn: "+919876543210",
    carrier: "Airtel",
    simTypeId: "st-1",
    simType: "M2M",
    status: "active",
    values: { apn: "m2m.airtel", sms_limit: "100" },
    createdAt: "2025-01-10T08:00:00.000Z",
    updatedAt: "2025-03-15T10:30:00.000Z",
  },
  {
    id: "sim-2",
    iccid: "8991100000000000002",
    msisdn: "+919876543211",
    carrier: "Jio",
    simTypeId: "st-2",
    simType: "Standard",
    status: "active",
    values: { apn: "internet.jio" },
    createdAt: "2025-02-01T09:00:00.000Z",
    updatedAt: "2025-02-01T09:00:00.000Z",
  },
  {
    id: "sim-3",
    iccid: "8991100000000000003",
    msisdn: "+919876543212",
    carrier: "Vi",
    simTypeId: "st-1",
    simType: "M2M",
    status: "suspended",
    values: { apn: "m2m.vi", sms_limit: "50" },
    createdAt: "2025-02-15T09:00:00.000Z",
    updatedAt: "2025-03-01T09:00:00.000Z",
  },
  {
    id: "sim-4",
    iccid: "8991100000000000004",
    msisdn: "+919876543213",
    carrier: "Airtel",
    simTypeId: "st-3",
    simType: "Roaming",
    status: "active",
    values: { apn: "roam.airtel", roaming_profile: "EU-NA" },
    createdAt: "2025-03-01T09:00:00.000Z",
    updatedAt: "2025-03-01T09:00:00.000Z",
  },
  {
    id: "sim-5",
    iccid: "8991100000000000005",
    msisdn: "+919876543214",
    carrier: "Jio",
    simTypeId: "st-2",
    simType: "Standard",
    status: "active",
    values: { apn: "internet.jio" },
    createdAt: "2025-04-01T09:00:00.000Z",
    updatedAt: "2025-04-01T09:00:00.000Z",
  },
];

function cloneSeed() {
  return SEED_SIMS.map((row) => ({
    ...row,
    values: { ...(row.values ?? {}) },
  }));
}

function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneSeed();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : cloneSeed();
  } catch {
    return cloneSeed();
  }
}

function saveStore(rows) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
}

function nextId() {
  return `sim-${Date.now().toString(36)}`;
}

function validatePayload(payload) {
  const iccid = payload.iccid?.trim();
  if (!iccid) throw new Error("ICCID is required");
  const msisdn = payload.msisdn?.trim();
  if (!msisdn) throw new Error("MSISDN is required");
  const carrier = payload.carrier?.trim();
  if (!carrier) throw new Error("Carrier is required");
  const simTypeId = payload.simTypeId?.trim();
  if (!simTypeId) throw new Error("SIM type is required");
  const status = payload.status?.trim();
  if (!SIM_STATUSES.includes(status)) throw new Error("Select a valid status");
  return {
    iccid,
    msisdn,
    carrier,
    simTypeId,
    simType: payload.simType?.trim() || "",
    status,
    values: payload.values ?? {},
  };
}

export async function listSims() {
  await delay(130);
  return loadStore().map((row) => ({ ...row, values: { ...(row.values ?? {}) } }));
}

export async function createSim(payload) {
  await delay(140);
  const data = validatePayload(payload);
  const rows = loadStore();
  if (rows.some((r) => r.iccid.toLowerCase() === data.iccid.toLowerCase())) {
    throw new Error("A SIM with this ICCID already exists");
  }
  const now = new Date().toISOString();
  const row = { id: nextId(), ...data, createdAt: now, updatedAt: now };
  rows.push(row);
  saveStore(rows);
  return { ...row, values: { ...data.values } };
}

export async function updateSim(simId, payload) {
  await delay(120);
  const data = validatePayload(payload);
  const rows = loadStore();
  const row = rows.find((r) => r.id === simId);
  if (!row) throw new Error("SIM not found");
  if (rows.some((r) => r.id !== simId && r.iccid.toLowerCase() === data.iccid.toLowerCase())) {
    throw new Error("A SIM with this ICCID already exists");
  }
  Object.assign(row, data, { updatedAt: new Date().toISOString() });
  row.values = { ...data.values };
  saveStore(rows);
  return { ...row, values: { ...row.values } };
}

export async function deleteSim(simId) {
  await delay(100);
  const rows = loadStore();
  const index = rows.findIndex((r) => r.id === simId);
  if (index < 0) throw new Error("SIM not found");
  rows.splice(index, 1);
  saveStore(rows);
}

export function formatSimTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return `${date} | ${time}`;
}
